import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Modal } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { fmtDate, fmtRelative } from '../lib/lookups';
import { useToast } from '../contexts/ToastContext';

export default function ApiTokensPage() {
  const toast = useToast();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actioning, setActioning] = useState(null);
  const [regenResult, setRegenResult] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi.get('/admin/api-tokens');
      setTokens(res.data || []);
      setError('');
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggle(t) {
    setActioning(t.id);
    try {
      const path = t.is_active ? `/admin/api-tokens/${t.id}/deactivate` : `/admin/api-tokens/${t.id}/activate`;
      await adminApi.post(path);
      toast.success(`Token ${t.is_active ? 'deactivated' : 'activated'}`);
      await load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setActioning(null);
    }
  }

  async function regenerate(t) {
    setActioning(t.id);
    try {
      const res = await adminApi.post(`/admin/api-tokens/${t.id}/regenerate`);
      setRegenResult(res.data);
      toast.success('Token regenerated. Copy it now — it will not be shown again.');
      await load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setActioning(null);
    }
  }

  async function doDelete() {
    if (!confirmDelete) return;
    setActioning(confirmDelete.id);
    try {
      await adminApi.delete(`/admin/api-tokens/${confirmDelete.id}`);
      toast.success('Token deleted');
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setActioning(null);
    }
  }

  function copy(text) {
    if (!text) return;
    navigator.clipboard?.writeText(text)
      .then(() => toast.success('Copied to clipboard'))
      .catch(() => toast.error('Could not copy. Select and copy manually.'));
  }

  return (
    <>
      <PageHeader
        title="API tokens"
        subtitle="Read-only bearer tokens for external voter guide websites. Public API exposes only published candidates."
        actions={
          <div className="d-flex gap-2">
            <Button
              as="a"
              href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/docs`}
              target="_blank"
              rel="noreferrer"
              variant="outline-secondary"
              data-testid="api-tokens-view-docs-button"
            >
              <i className="bi bi-journal-code me-1" /> View API Docs
            </Button>
            <Link to="/tokens/create">
              <Button variant="primary" data-testid="api-tokens-create-button">
                <i className="bi bi-plus-lg me-1" /> New token
              </Button>
            </Link>
          </div>
        }
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="info" className="d-flex gap-2 align-items-center">
          <i className="bi bi-info-circle" />
          <div>
            <strong>API tokens are READ-ONLY.</strong> They cannot create, update, or delete data; only published candidates are exposed via <code>/api/v1/*</code>.
            Admin sessions cannot access the public API, and API tokens cannot access admin endpoints.
          </div>
        </Alert>
        <Card data-testid="api-tokens-card">
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : tokens.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-key" />
              <div>No API tokens yet.</div>
              <Link to="/tokens/create"><Button variant="primary" className="mt-2">Create token</Button></Link>
            </div>
          ) : (
            <Table hover responsive size="sm" className="mb-0" data-testid="api-tokens-table">
              <thead>
                <tr>
                  <th>Name</th><th>Prefix</th><th>Status</th><th>Last used</th><th>Created</th><th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id} data-testid={`token-row-${t.id}`}>
                    <td>
                      <div className="fw-semibold">{t.name}</div>
                      {t.description && <div className="text-muted" style={{ fontSize: '0.78rem' }}>{t.description}</div>}
                    </td>
                    <td>
                      <code className="token-mono">{t.token_prefix}…</code>
                    </td>
                    <td>
                      {t.is_active ? (
                        <span className="status-badge status-published">Active</span>
                      ) : (
                        <span className="status-badge status-archived">Inactive</span>
                      )}
                    </td>
                    <td className="text-muted">{t.last_used_at ? fmtRelative(t.last_used_at) : <span className="text-muted">never</span>}</td>
                    <td className="text-muted">{fmtDate(t.created_at)}</td>
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end">
                        <Button size="sm" variant="outline-secondary" onClick={() => toggle(t)} disabled={actioning === t.id} data-testid={`token-toggle-${t.id}`}>
                          {t.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button size="sm" variant="outline-primary" onClick={() => regenerate(t)} disabled={actioning === t.id} data-testid={`token-regenerate-${t.id}`}>
                          Regenerate
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => setConfirmDelete(t)} disabled={actioning === t.id} data-testid={`token-delete-${t.id}`}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>

      <Modal show={!!regenResult} onHide={() => setRegenResult(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Token regenerated</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle me-2" />
            Copy this token now. For security, it will not be shown again.
          </Alert>
          <div className="d-flex gap-2 align-items-center">
            <code className="token-mono p-2 flex-grow-1" style={{ background: 'var(--app-surface-2)', borderRadius: 8 }} data-testid="regen-token-value">
              {regenResult?.token}
            </code>
            <Button variant="outline-secondary" onClick={() => copy(regenResult?.token)} data-testid="regen-token-copy">
              <i className="bi bi-clipboard me-1" /> Copy
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setRegenResult(null)}>Done</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!confirmDelete} onHide={() => setConfirmDelete(null)}>
        <Modal.Header closeButton><Modal.Title>Delete token</Modal.Title></Modal.Header>
        <Modal.Body>
          Delete <strong>{confirmDelete?.name}</strong>? Any external app using this token will immediately lose access.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="danger" onClick={doDelete} data-testid="confirm-delete-token-button">Delete</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
